import { useCallback, useEffect, useState } from "react";
import { ArrowRightOutlined, CaretRightFilled } from "@ant-design/icons";
import { toggleLinkPageReaction } from "../../controllers/LinkPageApi";
import { linkPageVisitorId } from "../../utils/linkPageTracking";
import { getLinkPageVideo } from "../../utils/linkPageMedia";
import { localIsoDate } from "./linkPageBuilderUtils";

// The visitor's own calendar day: a show stays listed through its evening.
const today = () => localIsoDate();
const eventDate = (value) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day))
    .toLocaleDateString("en-US", { month: "short", day: "2-digit", timeZone: "UTC" })
    .toUpperCase();
};

export function TickerBlock({ item }) {
  const lines = (item.links || []).filter((line) => line.title?.trim());
  if (!lines.length) return null;
  const run = (hidden) => (
    <span className="lpTicker__run" aria-hidden={hidden || undefined}>
      {lines.map((line) => (
        <span key={line.id} className="lpTicker__line">{line.title}</span>
      ))}
    </span>
  );
  return (
    <div className={`lpTicker ${item.variant === "subtle" ? "lpTicker--subtle" : ""}`}>
      <div className="lpTicker__track">
        {run(false)}
        {run(true)}
      </div>
    </div>
  );
}

export function EventsBlock({ item, disableLinks, onSelect, onClick }) {
  const upcoming = (item.links || [])
    .filter((event) => event.title && event.date && (disableLinks || event.date >= today()))
    .sort((left, right) => left.date.localeCompare(right.date));
  if (!upcoming.length) return null;
  return (
    <div className="lpEvents">
      {upcoming.map((event) => {
        const href = !disableLinks && !event.soldOut && event.url ? event.url : undefined;
        return (
          <a
            key={event.id}
            className={`lpEvents__row ${event.date < today() ? "lpEvents__row--past" : ""}`}
            href={href}
            target={href ? "_blank" : undefined}
            rel={href ? "noopener noreferrer" : undefined}
            onClick={(clickEvent) => {
              if (disableLinks) clickEvent.preventDefault();
              onSelect?.();
              if (href) onClick?.();
            }}
          >
            <span className="lpEvents__date">{eventDate(event.date)}</span>
            <span className="lpEvents__place">
              <strong>{event.title}</strong>
              {event.subtitle ? <small>{event.subtitle}</small> : null}
            </span>
            <span className={`lpEvents__status ${event.soldOut ? "lpEvents__status--soldOut" : ""}`}>
              {event.soldOut ? "Sold out" : event.url ? "Tickets" : ""}
            </span>
          </a>
        );
      })}
    </div>
  );
}

export function ArticlesBlock({ item, articles = [], disableLinks, onSelect, onClick }) {
  const picked = (item.value || "").split(",").filter(Boolean);
  const list = picked.length
    ? picked.map((id) => articles.find((article) => article.id === id)).filter(Boolean)
    : [...articles].sort((left, right) => new Date(right.date) - new Date(left.date)).slice(0, 3);
  if (!list.length) return null;
  return (
    <div className="lpArticles">
      {item.title ? <div className="lpEyebrow">{item.title}</div> : null}
      <div className="lpArticles__grid">
        {list.map((article) => (
          <a
            key={article.id}
            className="lpArticles__card"
            href={disableLinks ? undefined : `/newsPage/${article.id}`}
            onClick={(event) => {
              if (disableLinks) event.preventDefault();
              else onClick?.();
              onSelect?.();
            }}
          >
            <span className="lpArticles__cover">
              {article.thumbnail ? <img src={article.thumbnail} alt="" loading="lazy" /> : null}
            </span>
            {article.date ? (
              <small>
                {new Date(article.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </small>
            ) : null}
            <strong>{article.title}</strong>
          </a>
        ))}
      </div>
    </div>
  );
}

const reactedKey = (pageId, itemId) => `bonjourno-reacted:${pageId}:${itemId}`;
const readReacted = (pageId, itemId) => {
  try {
    return JSON.parse(window.localStorage.getItem(reactedKey(pageId, itemId)) || "[]");
  } catch {
    return [];
  }
};

const NO_COUNTS = {};

export function ReactionsBlock({ item, counts = NO_COUNTS, pageId, disableLinks, onSelect }) {
  const emojis = (item.value || "").split(",").filter(Boolean);
  const [state, setState] = useState(counts);
  const [reacted, setReacted] = useState([]);
  useEffect(() => setState(counts), [counts]);
  useEffect(() => {
    if (pageId) setReacted(readReacted(pageId, item.id));
  }, [pageId, item.id]);
  const react = async (emoji) => {
    onSelect?.();
    if (disableLinks || !pageId) return;
    const on = !reacted.includes(emoji);
    const next = on ? [...reacted, emoji] : reacted.filter((value) => value !== emoji);
    setReacted(next);
    setState((value) => ({ ...value, [emoji]: Math.max(0, (value[emoji] || 0) + (on ? 1 : -1)) }));
    try {
      const result = await toggleLinkPageReaction(pageId, {
        itemId: item.id,
        emoji,
        visitor: linkPageVisitorId(),
      });
      setState(result.counts || {});
      const confirmed = result.reacted
        ? [...new Set([...next, emoji])]
        : next.filter((value) => value !== emoji);
      setReacted(confirmed);
      window.localStorage.setItem(reactedKey(pageId, item.id), JSON.stringify(confirmed));
    } catch {
      setReacted(reacted);
      setState(state);
    }
  };
  const compact = (value) =>
    value >= 1000 ? `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k` : String(value || 0);
  return (
    <div className="lpReactions" role="group" aria-label={item.title || "Reactions"}>
      {emojis.map((emoji) => (
        <button
          key={emoji}
          type="button"
          aria-pressed={reacted.includes(emoji)}
          aria-label={`${emoji} ${compact(state[emoji])}`}
          className="lpReactions__button"
          onClick={() => react(emoji)}
        >
          <span aria-hidden="true">{emoji}</span>
          {compact(state[emoji])}
        </button>
      ))}
    </div>
  );
}

export function RowLink({ href, title, description, disableLinks, onClick, selected }) {
  return (
    <a
      className={`lpRow ${selected ? "lpRow--selected" : ""}`}
      href={href || undefined}
      target={href && !disableLinks ? "_blank" : undefined}
      rel={href && !disableLinks ? "noopener noreferrer" : undefined}
      tabIndex={disableLinks ? -1 : undefined}
      onClick={onClick}
    >
      <span className="lpRow__title">{title}</span>
      {description ? <span className="lpRow__meta">{description}</span> : null}
      <ArrowRightOutlined className="lpRow__arrow" aria-hidden="true" />
    </a>
  );
}

export function CoverMedia({ item, children, onSelect }) {
  return <CoverMediaSurface key={`${item?.type}:${item?.url}`} item={item} onSelect={onSelect}>{children}</CoverMediaSurface>;
}

function CoverMediaSurface({ item, children, onSelect }) {
  const [playing, setPlaying] = useState(false);
  const video = item?.type === "video" ? getLinkPageVideo(item.url) : null;
  const videoId = video?.embedUrl.split("/").pop();
  const [fallback, setFallback] = useState(false);
  // YouTube's large frame has no letterbox bars; older videos only have the small one.
  const videoImage = videoId ? `https://i.ytimg.com/vi/${videoId}/${fallback ? "hqdefault" : "maxresdefault"}.jpg` : "";
  const image = item?.type === "image" ? item.url : videoImage || item?.thumbnail;
  const inspectThumbnail = useCallback((node) => {
    // Cached/SSR images may finish before React attaches the load handler.
    // YouTube returns a 120px placeholder when the large frame is unavailable.
    if (videoId && !fallback && node?.complete && node.naturalWidth <= 120) setFallback(true);
  }, [videoId, fallback]);
  if (playing && video)
    return (
      <div className="lpCover lpCover--playing">
        <iframe
          src={`${video.embedUrl}?autoplay=1&playsinline=1`}
          title={item.title || "YouTube video"}
          allow="autoplay; accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    );
  return (
    <div className="lpCover" onClick={onSelect}>
      {image ? (
        <img
          ref={inspectThumbnail}
          className={`lpCover__image ${fallback ? "lpCover__image--letterboxed" : ""}`}
          src={image}
          alt={item?.type === "image" ? item.title || "" : ""}
          onLoad={(event) => inspectThumbnail(event.currentTarget)}
          onError={() => videoId && setFallback(true)}
        />
      ) : null}
      <span className="lpCover__shade" aria-hidden="true" />
      {video ? (
        <button
          type="button"
          className="lpCover__play"
          aria-label={`Play ${item.title || "video"}`}
          onClick={(event) => {
            event.stopPropagation();
            onSelect?.();
            setPlaying(true);
          }}
        >
          <CaretRightFilled aria-hidden="true" />
        </button>
      ) : null}
      <div className="lpCover__overlay">{children}</div>
    </div>
  );
}
